/**
 * TypeScript wire shapes for the /api/memory/ router — Sprint 12 Phase D.
 *
 * Mirrors backend/sherlock/schemas/memory.py field-for-field.
 * snake_case on the wire (see .claude/rules/api-contract.md).
 */

export type RuleStatus = 'proposed' | 'active' | 'retired';
export type FactStatus = 'active' | 'stale' | 'retired';
export type EpisodeSource = 'investigation' | 'analysis';
export type ThumbValue = -1 | 1;

export type RuleDomain = 'investigation' | 'review' | 'pm_report';

export const RULE_DOMAIN_LABELS: Record<RuleDomain, string> = {
  investigation: 'Investigate',
  review: 'Review',
  pm_report: 'Weekly report',
};

export interface ProceduralRuleOut {
  id: string;
  trigger_summary: string;
  rule_text: string;
  evidence_ids: string[];
  evidence_count: number;
  status: RuleStatus;
  domain: RuleDomain;
  prompt_version: string | null;
  promoted_at: string | null;
  promoted_by: string | null;
  retired_at: string | null;
  retired_by: string | null;
  created_at: string;
}

export type CaptureGapStatus = 'open' | 'addressed';

export interface CaptureGapOut {
  id: string;
  question: string;
  missing: string;
  suggested_source: string;
  asked_by: string;
  status: CaptureGapStatus;
  thread_id: string | null;
  created_at: string;
}

export interface PaginatedCaptureGaps {
  count: number;
  next: string | null;
  previous: string | null;
  results: CaptureGapOut[];
}

export interface InfraFactOut {
  id: string;
  topic: string;
  fact: string;
  evidence_ids: string[];
  evidence_count: number;
  first_observed: string;
  last_observed: string;
  status: FactStatus;
}

export interface CausalChain {
  symptom?: string | null;
  proximate_cause?: string | null;
  trigger?: string | null;
  root_cause?: string | null;
}

export interface InvestigationMemoryOut {
  id: string;
  source_type: EpisodeSource;
  source_id: string;
  anchor_from: string | null;
  anchor_to: string | null;
  services: string[];
  symptoms: string[];
  causal_chain: CausalChain;
  narrative_md: string;
  confidence: number | null;
  feedback_thumb: ThumbValue | null;
  feedback_tags: string[];
  created_at: string;
  archived_at: string | null;
}

export interface ImprovementDigestOut {
  generated_at: string | null;
  markdown: string | null;
}

export interface PaginatedRules {
  count: number;
  next: string | null;
  previous: string | null;
  results: ProceduralRuleOut[];
}

export interface PaginatedFacts {
  count: number;
  next: string | null;
  previous: string | null;
  results: InfraFactOut[];
}

export interface PaginatedEpisodes {
  count: number;
  next: string | null;
  previous: string | null;
  results: InvestigationMemoryOut[];
}
